import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import EmptyState from '../ui/EmptyState'
import ErrorState from '../ui/ErrorState'
import LoadingState from '../ui/LoadingState'
import SectionHeader from '../ui/SectionHeader'
import StatPill from '../ui/StatPill'
import { ADMIN_ISSUE_CATEGORIES, ADMIN_PERMISSION_MATRIX, ADMIN_REASON_CODES, PAY_STATUS_OPTIONS } from './adminConfig'
import { formatTitleText } from '../utils/displayText'
import { formatDateTime } from '../utils/dateFormatting'

const formatJsonPreview = (value) => {
  if (!value || typeof value !== 'object') return 'n/a'
  const keys = Object.keys(value)
  if (keys.length === 0) return 'n/a'
  const firstKey = keys[0]
  const firstValue = value[firstKey]
  return `${firstKey}: ${typeof firstValue === 'object' ? JSON.stringify(firstValue) : String(firstValue)}`
}

const loadOptionalRows = async (table, queryBuilder = null) => {
  try {
    let query = supabase.from(table).select('*')
    if (queryBuilder) {
      const shaped = queryBuilder(query)
      if (shaped) query = shaped
    }
    const response = await query
    if (response.error) return { error: response.error, data: [] }
    return { error: null, data: response.data || [] }
  } catch (error) {
    return { error, data: [] }
  }
}

export default function AdminAuditReportsSettingsPanel() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [auditRows, setAuditRows] = useState([])
  const [timeAuditRows, setTimeAuditRows] = useState([])
  const [accessHistoryRows, setAccessHistoryRows] = useState([])
  const [requestEventRows, setRequestEventRows] = useState([])
  const [payHistoryRows, setPayHistoryRows] = useState([])

  const [reportRows, setReportRows] = useState({
    missingPunches: [],
    disputedHours: [],
    unpaidPartialPay: [],
    manualAdjustments: [],
    unresolvedIssues: [],
    workforceExceptions: []
  })

  const load = useCallback(async () => {
    setLoading(true)
    setError('')

    const [
      adminAuditResponse,
      timeAuditResponse,
      accessHistoryResponse,
      requestEventsResponse,
      payHistoryResponse,
      timeEntriesResponse,
      payRecordsResponse,
      issuesResponse,
      profilesResponse,
      workersResponse
    ] = await Promise.all([
      loadOptionalRows('admin_action_audit', (query) => query.order('created_at', { ascending: false }).limit(250)),
      loadOptionalRows('employee_time_entry_audit', (query) => query.order('created_at', { ascending: false }).limit(250)),
      loadOptionalRows('profile_access_status_history', (query) => query.order('created_at', { ascending: false }).limit(250)),
      loadOptionalRows('employee_request_events', (query) => query.order('created_at', { ascending: false }).limit(250)),
      loadOptionalRows('pay_status_history', (query) => query.order('created_at', { ascending: false }).limit(250)),
      loadOptionalRows('employee_time_entries', (query) => query.order('updated_at', { ascending: false }).limit(300)),
      loadOptionalRows('pay_status_records', (query) => query.order('updated_at', { ascending: false }).limit(300)),
      loadOptionalRows('admin_issues', (query) => query.order('updated_at', { ascending: false }).limit(300)),
      loadOptionalRows('profiles', (query) => query.select('id, access_role, work_status, worker_id, display_name').limit(300)),
      loadOptionalRows('workers', (query) => query.select('id, name, role, is_active, work_status').limit(300))
    ])

    const criticalError = [timeEntriesResponse, profilesResponse, workersResponse].find((response) => response.error)
    if (criticalError?.error) {
      setLoading(false)
      setError('Unable to load admin audit/report foundation data: ' + criticalError.error.message)
      return
    }

    const timeEntries = timeEntriesResponse.data || []
    const payRecords = payRecordsResponse.data || []
    const issues = issuesResponse.data || []
    const profiles = profilesResponse.data || []
    const workers = workersResponse.data || []
    const workersById = new Map(workers.map((worker) => [String(worker.id), worker]))

    const missingPunches = timeEntries.filter((entry) => !entry.clocked_in_at || !entry.clocked_out_at)
    const disputedHours = timeEntries.filter((entry) => String(entry.approval_status || '').toLowerCase() === 'disputed')
    const unpaidPartialPay = payRecords.filter((record) => ['unpaid', 'partial', 'disputed'].includes(String(record.payment_status || '').toLowerCase()))
    const manualAdjustments = timeEntries.filter((entry) => entry.manual_adjustment === true)
    const unresolvedIssues = issues.filter((issue) => !['resolved', 'closed'].includes(String(issue.resolution_status || '').toLowerCase()))

    const workforceExceptions = profiles.filter((profile) => {
      const worker = profile.worker_id ? workersById.get(String(profile.worker_id)) : null
      if (!profile.worker_id) return true
      if (!worker) return true
      if (worker.is_active === false) return true
      if (String(worker.work_status || '').toLowerCase() !== 'active') return true
      if (String(profile.work_status || '').toLowerCase() !== 'active') return true
      return false
    })

    setAuditRows(adminAuditResponse.data || [])
    setTimeAuditRows(timeAuditResponse.data || [])
    setAccessHistoryRows(accessHistoryResponse.data || [])
    setRequestEventRows(requestEventsResponse.data || [])
    setPayHistoryRows(payHistoryResponse.data || [])

    setReportRows({
      missingPunches,
      disputedHours,
      unpaidPartialPay,
      manualAdjustments,
      unresolvedIssues,
      workforceExceptions
    })

    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const combinedAuditFeed = useMemo(() => {
    const rows = []

    auditRows.forEach((row) => {
      rows.push({
        source: 'admin_action_audit',
        id: row.id,
        eventAt: row.created_at,
        actor: row.actor_profile_id || 'unknown',
        action: row.action_type || 'admin_action',
        entity: `${row.entity_type || 'record'}:${row.entity_id || 'unknown'}`,
        beforeValue: row.before_value,
        afterValue: row.after_value,
        reason: row.reason || ''
      })
    })

    accessHistoryRows.forEach((row) => {
      rows.push({
        source: 'profile_access_status_history',
        id: row.id,
        eventAt: row.created_at,
        actor: row.actor_profile_id || 'unknown',
        action: 'access_status_change',
        entity: `profile:${row.profile_id || 'unknown'}`,
        beforeValue: {
          access_role: row.previous_access_role,
          work_status: row.previous_work_status
        },
        afterValue: {
          access_role: row.next_access_role,
          work_status: row.next_work_status
        },
        reason: row.reason_note || row.reason_code || ''
      })
    })

    timeAuditRows.forEach((row) => {
      rows.push({
        source: 'employee_time_entry_audit',
        id: row.id,
        eventAt: row.created_at,
        actor: row.actor_profile_id || 'unknown',
        action: row.action_type || 'time_entry_change',
        entity: `time_entry:${row.time_entry_id || 'unknown'}`,
        beforeValue: row.before_state,
        afterValue: row.after_state,
        reason: row.reason_code || row.reason_note || ''
      })
    })

    payHistoryRows.forEach((row) => {
      rows.push({
        source: 'pay_status_history',
        id: row.id,
        eventAt: row.created_at,
        actor: row.actor_profile_id || 'unknown',
        action: 'pay_status_change',
        entity: `pay_status:${row.pay_status_id || 'unknown'}`,
        beforeValue: {
          payment_status: row.previous_payment_status,
          amount_paid: row.previous_amount_paid,
          amount_owed: row.previous_amount_owed
        },
        afterValue: {
          payment_status: row.next_payment_status,
          amount_paid: row.next_amount_paid,
          amount_owed: row.next_amount_owed
        },
        reason: row.reason || ''
      })
    })

    requestEventRows.forEach((row) => {
      rows.push({
        source: 'employee_request_events',
        id: row.id,
        eventAt: row.created_at,
        actor: row.actor_profile_id || 'unknown',
        action: row.event_type || 'request_event',
        entity: `request:${row.request_id || 'unknown'}`,
        beforeValue: {
          status: row.from_status,
          stage: row.from_stage
        },
        afterValue: {
          status: row.to_status,
          stage: row.to_stage
        },
        reason: row.note || ''
      })
    })

    return rows
      .filter((row) => row.eventAt)
      .sort((a, b) => new Date(b.eventAt).getTime() - new Date(a.eventAt).getTime())
      .slice(0, 150)
  }, [accessHistoryRows, auditRows, payHistoryRows, requestEventRows, timeAuditRows])

  if (loading) {
    return <LoadingState title="Loading audit and reports" message="Building audit/history and report foundations." />
  }

  if (error) {
    return <ErrorState title="Audit/report foundation unavailable" message={error} />
  }

  return (
    <section className="gg-module-stack">
      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Admin"
          title="Audit / History Foundation"
          description="Unified history across access, time adjustments, request events, pay-status changes, and admin corrections."
        />
        <div className="gg-tool-pills">
          <StatPill label="Audit Events" value={combinedAuditFeed.length} tone={combinedAuditFeed.length ? 'info' : 'warning'} />
          <StatPill label="Manual Adjustments" value={reportRows.manualAdjustments.length} tone={reportRows.manualAdjustments.length ? 'warning' : 'info'} />
          <StatPill label="Unresolved Issues" value={reportRows.unresolvedIssues.length} tone={reportRows.unresolvedIssues.length ? 'warning' : 'info'} />
        </div>
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Recent Sensitive Actions</p>
        {combinedAuditFeed.length === 0 ? (
          <EmptyState title="No audit events in scope" message="Audit feed is available and will populate as sensitive actions occur." />
        ) : (
          <div className="gg-record-list">
            {combinedAuditFeed.slice(0, 40).map((row) => (
              <article key={`${row.source}:${row.id}:${row.eventAt}`} className="gg-record-item">
                <p className="gg-record-item__title">{formatTitleText(row.action, 'Action')} • {row.entity}</p>
                <p className="gg-record-item__meta">Source {row.source} • Actor {row.actor}</p>
                <p className="gg-record-item__meta">Before {formatJsonPreview(row.beforeValue)} • After {formatJsonPreview(row.afterValue)}</p>
                <p className="gg-record-item__meta">Reason {row.reason || 'Not provided'} • {formatDateTime(row.eventAt)}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Reports"
          title="Operations Exception Reports"
          description="Filter-ready tables for missing punches, disputed hours, unpaid/partial pay, manual adjustments, workforce exceptions, and unresolved issues."
        />
        <div className="gg-tool-pills">
          <StatPill label="Missing Punches" value={reportRows.missingPunches.length} tone={reportRows.missingPunches.length ? 'warning' : 'info'} />
          <StatPill label="Disputed Hours" value={reportRows.disputedHours.length} tone={reportRows.disputedHours.length ? 'error' : 'info'} />
          <StatPill label="Unpaid/Partial Pay" value={reportRows.unpaidPartialPay.length} tone={reportRows.unpaidPartialPay.length ? 'warning' : 'info'} />
          <StatPill label="Workforce Exceptions" value={reportRows.workforceExceptions.length} tone={reportRows.workforceExceptions.length ? 'warning' : 'info'} />
        </div>

        <div className="gg-record-list">
          <article className="gg-record-item">
            <p className="gg-record-item__title">Missing Punches</p>
            <p className="gg-record-item__meta">{reportRows.missingPunches.length} entries missing punch-in or punch-out.</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Disputed Hours</p>
            <p className="gg-record-item__meta">{reportRows.disputedHours.length} entries are in disputed review status.</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Unpaid / Partial Pay</p>
            <p className="gg-record-item__meta">{reportRows.unpaidPartialPay.length} pay records need follow-up.</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Manual Adjustments</p>
            <p className="gg-record-item__meta">{reportRows.manualAdjustments.length} manual time adjustments logged.</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Unresolved Issues</p>
            <p className="gg-record-item__meta">{reportRows.unresolvedIssues.length} issue records are still open/in progress/blocked.</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Workforce Exceptions</p>
            <p className="gg-record-item__meta">{reportRows.workforceExceptions.length} profile/worker linkage lifecycle mismatches.</p>
          </article>
        </div>
      </section>

      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Settings"
          title="Admin Config Visibility"
          description="Reference surface for role definitions, issue categories, pay labels, reason codes, and section permissions matrix."
        />

        <div className="gg-record-list">
          <article className="gg-record-item">
            <p className="gg-record-item__title">Issue Categories</p>
            <p className="gg-record-item__meta">{ADMIN_ISSUE_CATEGORIES.map((item) => item.label).join(' • ')}</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Pay Status Labels</p>
            <p className="gg-record-item__meta">{PAY_STATUS_OPTIONS.map((item) => item.label).join(' • ')}</p>
          </article>
          <article className="gg-record-item">
            <p className="gg-record-item__title">Reason Codes</p>
            <p className="gg-record-item__meta">{ADMIN_REASON_CODES.map((item) => item.label).join(' • ')}</p>
          </article>
        </div>

        <p className="gg-module-panel__label">Section Permissions Matrix</p>
        <div className="gg-record-list">
          {ADMIN_PERMISSION_MATRIX.map((row) => (
            <article key={row.accessRole} className="gg-record-item">
              <p className="gg-record-item__title">{row.accessRoleLabel}</p>
              <p className="gg-record-item__meta">
                Dashboard {row.dashboard ? 'Yes' : 'No'} • Access {row.access ? 'Yes' : 'No'} • Workforce {row.workforce ? 'Yes' : 'No'} • Contacts {row.contacts ? 'Yes' : 'No'}
              </p>
              <p className="gg-record-item__meta">
                Time/Pay {row.time_pay ? 'Yes' : 'No'} • Issues {row.issues ? 'Yes' : 'No'} • Corrections {row.corrections ? 'Yes' : 'No'} • Reports {row.reports ? 'Yes' : 'No'}
              </p>
            </article>
          ))}
        </div>
      </section>
    </section>
  )
}
